import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

// Self-service presence for every logged-in agent, regardless of WFM page
// access. GET /wfm/status/me returns the current status plus the break/email
// allowances that apply to this agent and what they've used today, so the
// status menu never hard-codes the workspace's break policy.
//
// Allowances are refreshed on a timer as well as after each change: an open
// break burns its allowance while it runs.
const REFRESH_MS = 60 * 1000;

export function useAgentStatus() {
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const userId = user?._id;
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const refresh = useCallback(() => {
    if (!userId) return;
    api.get('/wfm/status/me')
      .then(r => { if (mounted.current) setDetail(r.data); })
      .catch(() => {});
  }, [userId]);

  useEffect(() => {
    refresh();
    if (!userId) return undefined;
    const id = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(id);
  }, [refresh, userId]);

  // Returns the new detail on success; throws with a readable message when the
  // server refuses (e.g. the daily break allowance is used up).
  const changeStatus = useCallback(async (status, reason) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.patch('/wfm/status', { status, reason });
      setDetail(data);
      return data;
    } catch (err) {
      const message = err.response?.data?.error || 'Could not change status';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    detail,
    status: detail?.status || 'offline',
    reason: detail?.reason || null,
    since: detail?.since || null,
    online: !!detail?.online,
    policy: detail?.policy || null,
    usage: detail?.usage || null,
    notReadyReasons: detail?.not_ready_reasons || [],
    loading,
    error,
    changeStatus,
    refresh,
  };
}
